import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import "./LandingPage.css";
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  SunIcon,
  MoonIcon,
  UserIcon,
  BriefcaseIcon,
  TrophyIcon,
  ShieldIcon,
  SearchIcon,
  ClockIcon
} from '../userDashboard/theme';
const logoImg = process.env.PUBLIC_URL + '/702844604_2344407339420357_5425605054250800936_n.jpg';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const LandingPage = () => {
  const { C, isDark, toggleTheme } = useTheme();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  
  // État pour gérer l'ouverture/fermeture du menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleDashboard = () => {
    setIsMobileMenuOpen(false);
    if (user?.role === 'company') {
      navigate('/company');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className='landing-wrapper' style={{ background: C.bgDeep, color: C.textPrimary, overflowX: 'hidden' }}>

      {/* Navigation */}
      <nav className='lp-navbar' style={{
        background: isDark ? 'rgba(6, 0, 22, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        borderBottom: `1px solid ${C.border}`
      }}>
        <div className='lp-nav-container'>
          <div className='lp-nav-left'>
            <img src={logoImg} alt="SkillHunt Logo" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
            <span className='lp-logo-text'>SkillHunt</span>
          </div>

          {/* Liens de navigation Desktop */}
          <div className='lp-nav-links'>
            <a href="#how-it-works" className='lp-nav-link' style={{ color: C.textPrimary }}>How it Works</a>
            <a href="#features" className='lp-nav-link' style={{ color: C.textPrimary }}>Features</a>
            <a href="#pricing" className='lp-nav-link' style={{ color: C.textPrimary }}>Pricing</a>
          </div>

          <div className='lp-nav-right'>
            {/* Bouton Thème Switcher */}
            <button
              onClick={toggleTheme}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: isDark ? '#fbbf24' : '#6366f1',
                display: 'flex',
                alignItems: 'center',
                justifycontent: 'center',
                padding: '8px',
                borderRadius: '50%',
                transition: '0.3s'
              }}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </button>

            {/* Boutons d'authentification Desktop */}
            <div className="lp-nav-actions-desktop">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={handleDashboard}
                    className='lp-login-btn'
                    style={{ color: C.textPrimary }}
                  >
                    Dashboard
                  </button>
                  <button onClick={handleLogout} className='lp-get-started-btn'>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <button className='lp-login-btn' style={{ color: C.textPrimary }}>Login</button>
                  </Link>
                  <Link to="/register?type=user">
                    <button className='lp-get-started-btn'>Get Started</button>
                  </Link>
                </>
              )}
            </div>

            {/* Bouton Burger Mobile */}
            <button 
              className="lp-burger-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ color: C.textPrimary }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Menu Tiroir Mobile Réactif (AnimatePresence) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Arrière-plan flouté sombre */}
            <motion.div 
              className="lp-mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Panneau latéral coulissant */}
            <motion.div 
              className="lp-mobile-drawer"
              style={{ background: C.bgCard, borderLeft: `1px solid ${C.border}` }}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
            >
              <div className="lp-drawer-header">
                <span className='lp-logo-text'>Menu</span>
                <button className="lp-close-btn" onClick={() => setIsMobileMenuOpen(false)} style={{ color: C.textPrimary }}>✕</button>
              </div>
              
              <div className="lp-drawer-links">
                <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)} style={{ color: C.textPrimary }}>How it Works</a>
                <a href="#features" onClick={() => setIsMobileMenuOpen(false)} style={{ color: C.textPrimary }}>Features</a>
                <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)} style={{ color: C.textPrimary }}>Pricing</a>
                
                <hr style={{ borderColor: C.border, opacity: 0.5, margin: '1rem 0' }} />
                
                {isAuthenticated ? (
                  <div className="lp-drawer-buttons">
                    <button onClick={handleDashboard} className="lp-login-btn" style={{ color: C.textPrimary, width: '100%' }}>Dashboard</button>
                    <button onClick={handleLogout} className="lp-get-started-btn" style={{ width: '100%' }}>Logout</button>
                  </div>
                ) : (
                  <div className="lp-drawer-buttons">
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} style={{ width: '100%' }}>
                      <button className="lp-login-btn" style={{ color: C.textPrimary, width: '100%' }}>Login</button>
                    </Link>
                    <Link to="/register?type=user" onClick={() => setIsMobileMenuOpen(false)} style={{ width: '100%' }}>
                      <button className="lp-get-started-btn" style={{ width: '100%' }}>Get Started</button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <header className='lp-hero'>
        <img 
          src="/Professional team collaborating on a project.svg" 
          alt="Collaboration" 
          className='lp-hero-bg'
        />
        <motion.div 
          className='lp-hero-content'
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <div className='lp-hero-badge'>
            Trusted by 500+ companies worldwide
          </div>
          <h1 className='lp-hero-title'>
            <span>Prove Your Skills,</span>
            <span className='lp-gradient-text'>Get Hired.</span>
          </h1>
          <p className='lp-hero-description'>
            Skip the resume black hole. Showcase your real abilities through skill tests and challenges. 
            Companies hire based on proven talent, not just credentials.
          </p>
          <div className='lp-hero-actions'>
            <Link to="/register?type=user">
              <button className='lp-btn-primary'>Start Testing Your Skills</button>
            </Link>
            <Link to="/register?type=company">
              <button className='lp-btn-secondary' style={{ color: C.textPrimary }}>
                <BriefcaseIcon /> For Companies
              </button>
            </Link>
          </div>
        </motion.div>
      </header>

      {/* Stats Section */}
      <motion.div 
        className='lp-stats-row'
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <StatCard 
          icon={<UserIcon />} 
          value="10K+" 
          label="Active Users" 
          C={C}
          iconColor="#6366f1"
          iconBg="rgba(99, 102, 241, 0.1)"
        />
        <StatCard 
          icon={<BriefcaseIcon />} 
          value="500" 
          label="Partner Companies" 
          C={C}
          iconColor="#2dd4bf"
          iconBg="rgba(45, 212, 191, 0.1)"
        />
        <StatCard 
          icon={<TrophyIcon />} 
          value="85%" 
          label="Success Rate" 
          C={C}
          iconColor="#a855f7"
          iconBg="rgba(168, 85, 247, 0.1)"
        />
      </motion.div>

      {/* How it Works Section */}
      <section id="how-it-works" className='lp-section-linear'>
        <span className='lp-section-tag'>How it Works</span>
        <h2 className='lp-section-title' style={{ color: C.textPrimary }}>
          Simple Process, <span className='lp-gradient-text'>Powerful Results</span>
        </h2>
        <p className='lp-section-subtitle' style={{ color: C.textSecond }}>
          Whether you're a job seeker or a hiring company, our platform makes skill-based hiring effortless.
        </p>

        {/* Bento Box horizontale unique contenant les étapes */}
        <motion.div 
          className='lp-linear-process-card'
          style={{ background: C.bgCard }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className='lp-linear-steps-container'>
            
            {/* Étape 1 */}
            <div className='lp-linear-step'>
              <div className='lp-step-icon-wrapper wrapper-blue'>
                <span className='lp-step-icon-badge'>1</span>
                <UserIcon />
              </div>
              <h3 className='lp-step-linear-title' style={{ color: C.textPrimary }}>1. Create Profile</h3>
              <p className='lp-step-linear-desc' style={{ color: C.textSecond }}>Sign up and showcase your skills or company goals.</p>
            </div>

            {/* Étape 2 */}
            <div className='lp-linear-step'>
              <div className='lp-step-icon-wrapper wrapper-orange'>
                <span className='lp-step-icon-badge'>2</span>
                <BriefcaseIcon />
              </div>
              <h3 className='lp-step-linear-title' style={{ color: C.textPrimary }}>2. Custom Challenges</h3>
              <p className='lp-step-linear-desc' style={{ color: C.textSecond }}>Take verified skill tests or build custom assessments.</p>
            </div>

            {/* Étape 3 */}
            <div className='lp-linear-step'>
              <div className='lp-step-icon-wrapper wrapper-yellow'>
                <span className='lp-step-icon-badge'>3</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
              </div>
              <h3 className='lp-step-linear-title' style={{ color: C.textPrimary }}>3. Review & Match</h3>
              <p className='lp-step-linear-desc' style={{ color: C.textSecond }}>Access real-time skill insights and detailed metrics.</p>
            </div>

            {/* Étape 4 */}
            <div className='lp-linear-step'>
              <div className='lp-step-icon-wrapper wrapper-purple'>
                <span className='lp-step-icon-badge'>4</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <h3 className='lp-step-linear-title' style={{ color: C.textPrimary }}>4. Hire & Land Job</h3>
              <p className='lp-step-linear-desc' style={{ color: C.textSecond }}>Connect directly based on data-driven matching.</p>
            </div>

          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className='lp-section' style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
        <span className='lp-section-tag'>Why SkillHunt</span>
        <h2 className='lp-section-title' style={{ color: C.textPrimary }}>Built for the Future <span className='lp-gradient-text'>of Hiring</span></h2>
        <p className='lp-section-subtitle' style={{ color: C.textSecond }}>
          We're revolutionizing how talent meets opportunity with a platform built on fairness and transparency.
        </p>

        <div className='lp-features-grid'>
          <FeatureCard 
            icon={<SearchIcon color="#6366f1" size={24} />}
            title="Skill-Based Hiring"
            desc="Move beyond resume screening. Our platform evaluates real abilities through practical tests and challenges."
            items={["Practical assessments", "Real-world scenarios", "Verified skill badges"]}
            C={C}
            iconBg="rgba(99, 102, 241, 0.1)"
          />
          <FeatureCard 
            icon={<ShieldIcon color="#2dd4bf" />}
            title="Fair Opportunity"
            desc="Every candidate gets an equal chance to shine. No more bias from fancy degrees or company names."
            items={["Blind assessments", "Equal playing field", "Merit-based matching"]}
            C={C}
            iconBg="rgba(45, 212, 191, 0.1)"
          />
          <FeatureCard 
            icon={<ClockIcon color="#a855f7" />}
            title="Streamlined Process"
            desc="Cut hiring time in half. Our intelligent matching system connects the right candidates with the right roles."
            items={["AI-powered matching", "Instant notifications", "One-click scheduling"]}
            C={C}
            iconBg="rgba(168, 85, 247, 0.1)"
          />
        </div>
      </section>

      {/* Final CTA */}
      <motion.div 
        className='lp-cta-block' 
        style={{ background: isDark ? C.bgCard : '#fff', border: `1px solid ${C.border}` }}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
      >
        <h2 className='lp-cta-title' style={{ color: C.textPrimary }}>Ready to Get Started?</h2>
        <p className='lp-cta-desc' style={{ color: C.textSecond }}>
          Join thousands of candidates and companies transforming the way hiring works. Create your free account today.
        </p>
        <Link to="/register?type=user">
          <button className='lp-cta-btn'>Create Your Free Account</button>
        </Link>
        <p className='lp-cta-note' style={{ color: C.textMuted }}>No credit card required. Free forever for candidates.</p>
      </motion.div>

      <footer className='lp-footer' style={{ color: C.textMuted }}>
        &copy; 2026 SkillHunt — All rights reserved.
      </footer>
    </div>
  );
};

/* Sous-composants internes */
const StatCard = ({ icon, value, label, C, iconColor, iconBg }) => (
  <motion.div className='lp-stat-card' style={{ background: C.bgCard }} variants={fadeUp}>
    <div className='lp-stat-icon' style={{ background: iconBg, color: iconColor }}>
      {icon}
    </div>
    <div className='lp-stat-value' style={{ color: C.accent }}>{value}</div>
    <div className='lp-stat-label' style={{ color: C.textSecond }}>{label}</div>
  </motion.div>
);

const FeatureCard = ({ icon, title, desc, items, C, iconBg }) => (
  <motion.div className='lp-feature-card' style={{ background: C.bgCard }} variants={fadeUp}>
    <div className='lp-feature-icon' style={{ background: iconBg }}>
      {icon}
    </div>
    <h3 style={{ color: C.textPrimary }}>{title}</h3>
    <p style={{ color: C.textSecond }}>{desc}</p>
    <div className='lp-feature-list'>
      {items.map(item => (
        <div key={item} className='lp-feature-item' style={{ color: C.textPrimary }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1' }} />
          {item}
        </div>
      ))}
    </div>
  </motion.div>
);

export default LandingPage;